import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Sun, LayoutDashboard, FileText, MessageSquare, LogOut, 
  Plus, Trash2, Edit, Mail, Phone, Calendar,
  Menu, X, Star, CalendarCheck, BarChart3, TrendingUp, Eye, Users, MessageCircle, Shield
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { safeHtml } from '../lib/sanitize';
import { renderBlogContent } from '../lib/renderBlogContent';
import '../styles/blog-content.css';  // Permanent .virat-blog theme for admin blog preview panel.
import AdminSecurityTab from './AdminSecurityTab';
import SeoBackfillBanner from '../components/admin/SeoBackfillBanner';

const API = process.env.REACT_APP_BACKEND_URL;

// Axios interceptor for auto token refresh
const apiClient = axios.create();
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const token = localStorage.getItem('token');
        if (token) {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return apiClient(originalRequest);
        }
      } catch (refreshErr) {
        // Silently fail - don't redirect
      }
    }
    return Promise.reject(error);
  }
);

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [blogs, setBlogs] = useState([]);
  const [showBlogForm, setShowBlogForm] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [blogForm, setBlogForm] = useState({
    title: '', excerpt: '', content: '', image_url: '', category: 'General', published: false, scheduled_at: ''
  });
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [kundliRequests, setKundliRequests] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [crmStats, setCrmStats] = useState(null);
  const [followUps, setFollowUps] = useState([]);
  const [sendWAPhone, setSendWAPhone] = useState('');
  const [sendWAMessage, setSendWAMessage] = useState('');
  const [sendingWA, setSendingWA] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [reviewsList, setReviewsList] = useState([]);
  const [newReview, setNewReview] = useState({ name: '', location: '', message: '', rating: 5, source: 'google' });
  const [showPreview, setShowPreview] = useState(false);

  const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  useEffect(() => {
    if (activeTab === 'blogs') fetchBlogs();
    if (activeTab === 'messages') fetchMessages();
    if (activeTab === 'kundli') fetchKundliRequests();
    if (activeTab === 'consultations') fetchConsultations();
    if (activeTab === 'crm') { fetchConsultations(); fetchCrmStats(); fetchFollowUps(); }
    if (activeTab === 'dashboard') { fetchBlogs(); fetchMessages(); fetchKundliRequests(); fetchConsultations(); fetchCrmStats(); }
    if (activeTab === 'analytics') { fetchAnalytics(); }
    if (activeTab === 'reviews') { fetchReviews(); }
  }, [activeTab]);

  const fetchBlogs = async () => {
    try {
      const response = await apiClient.get(`${API}/api/blogs?published_only=false`, getAuthHeaders());
      setBlogs(response.data);
    } catch (error) { console.error('Error fetching blogs:', error); }
  };

  const fetchMessages = async () => {
    try {
      const response = await apiClient.get(`${API}/api/contact`, getAuthHeaders());
      setMessages(response.data);
    } catch (error) { console.error('Error fetching messages:', error); }
  };

  const fetchKundliRequests = async () => {
    try {
      const response = await apiClient.get(`${API}/api/kundli`, getAuthHeaders());
      setKundliRequests(response.data);
    } catch (error) { console.error('Error fetching kundli requests:', error); }
  };

  const fetchConsultations = async () => {
    try {
      const response = await apiClient.get(`${API}/api/consultations`, getAuthHeaders());
      setConsultations(response.data);
    } catch (error) { console.error('Error fetching consultations:', error); }
  };

  const updateConsultationStatus = async (id, status) => {
    try {
      await apiClient.put(`${API}/api/consultations/${id}/status`, { status }, getAuthHeaders());
      fetchConsultations();
      if (activeTab === 'crm') fetchCrmStats();
    } catch (error) { console.error('Error updating status:', error); }
  };

  const updateLeadDetails = async (id, data) => {
    try {
      await apiClient.put(`${API}/api/consultations/${id}/status`, data, getAuthHeaders());
      fetchConsultations();
    } catch (error) { console.error('Error updating lead:', error); }
  };

  const fetchCrmStats = async () => {
    try {
      const res = await apiClient.get(`${API}/api/crm/stats`, getAuthHeaders());
      setCrmStats(res.data);
    } catch (error) { console.error('Error fetching CRM stats:', error); }
  };

  const fetchFollowUps = async () => {
    try {
      const res = await apiClient.get(`${API}/api/crm/follow-ups`, getAuthHeaders());
      setFollowUps(res.data);
    } catch (error) { console.error('Error fetching follow-ups:', error); }
  };

  const fetchAnalytics = async () => {
    try {
      const res = await apiClient.get(`${API}/api/analytics/dashboard`, getAuthHeaders());
      setAnalyticsData(res.data);
    } catch (error) { console.error('Error fetching analytics:', error); }
  };

  const fetchReviews = async () => {
    try {
      const res = await apiClient.get(`${API}/api/reviews`);
      setReviewsList(res.data);
    } catch (error) { console.error('Error fetching reviews:', error); }
  };

  const addReview = async () => {
    if (!newReview.name || !newReview.message) return;
    try {
      await apiClient.post(`${API}/api/reviews`, newReview, getAuthHeaders());
      setNewReview({ name: '', location: '', message: '', rating: 5, source: 'google' });
      fetchReviews();
    } catch (error) { console.error('Error adding review:', error); }
  };

  const deleteReview = async (id) => {
    if (!window.confirm('Delete this review?')) return;
    try {
      await apiClient.delete(`${API}/api/reviews/${id}`, getAuthHeaders());
      fetchReviews();
    } catch (error) { console.error('Error deleting review:', error); }
  };

  const processFollowUps = async () => {
    try {
      const res = await apiClient.post(`${API}/api/crm/process-follow-ups`, {}, getAuthHeaders());
      alert(`${res.data.sent} follow-ups sent!`);
      fetchFollowUps();
    } catch (error) { console.error('Error processing follow-ups:', error); }
  };

  const sendManualWA = async () => {
    if (!sendWAPhone || !sendWAMessage) return;
    setSendingWA(true);
    try {
      await apiClient.post(`${API}/api/crm/send-whatsapp`, { phone: sendWAPhone, message: sendWAMessage }, getAuthHeaders());
      alert('WhatsApp message sent!');
      setSendWAPhone('');
      setSendWAMessage('');
    } catch (error) {
      alert('Failed to send WhatsApp');
    } finally {
      setSendingWA(false);
    }
  };

  const handleLogout = async () => { await logout(); navigate('/admin/login'); };

  const handleBlogSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBlog) {
        await apiClient.put(`${API}/api/blogs/${editingBlog.id}`, blogForm, getAuthHeaders());
        alert('Blog updated successfully!');
      } else {
        await apiClient.post(`${API}/api/blogs`, blogForm, getAuthHeaders());
        alert('Blog created successfully!');
      }
      setShowBlogForm(false);
      setEditingBlog(null);
      setBlogForm({ title: '', excerpt: '', content: '', image_url: '', category: 'General', published: false, scheduled_at: '' });
      // 🐛 Feb 07, 2026 (P0 fix): Refetch blog list so the UI reflects the
      // latest saved image_url / excerpt / content instead of showing the
      // stale pre-edit values from local state. Previously users thought
      // save was failing when in fact the DB was updated but the list
      // never refreshed.
      await fetchBlogs();
    } catch (error) {
      console.error('Error saving blog:', error);
      alert('Blog save failed: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteBlog = async (blogId) => {
    if (window.confirm('Delete this blog?')) {
      try { await apiClient.delete(`${API}/api/blogs/${blogId}`, getAuthHeaders()); fetchBlogs(); }
      catch (error) { console.error('Error deleting blog:', error); }
    }
  };

  const handleEditBlog = (blog) => {
    setEditingBlog(blog);
    setBlogForm({ title: blog.title, excerpt: blog.excerpt, content: blog.content, image_url: blog.image_url || '', category: blog.category, published: blog.published || false, scheduled_at: blog.scheduled_at || '' });
    setShowBlogForm(true);
  };

  const handleDeleteMessage = async (messageId) => {
    if (window.confirm('Delete this message?')) {
      try { await apiClient.delete(`${API}/api/contact/${messageId}`, getAuthHeaders()); fetchMessages(); setSelectedMessage(null); }
      catch (error) { console.error('Error deleting message:', error); }
    }
  };

  const [generatingBlog, setGeneratingBlog] = useState(false);
  const [showAIPrompt, setShowAIPrompt] = useState(false);
  const [aiTopic, setAITopic] = useState('');
  const [aiLang, setAILang] = useState('hi');
  const [generatingImage, setGeneratingImage] = useState(false);

  const handleGenerateImage = async () => {
    const topic = blogForm.title.trim();
    if (!topic) {
      window.alert('पहले Blog का Title डालें!');
      return;
    }
    setGeneratingImage(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/api/blogs/generate-image`, { topic, excerpt: blogForm.excerpt || '' }, { 
        headers: { Authorization: `Bearer ${token}` },
        timeout: 90000
      });
      if (res.data?.image_url) {
        setBlogForm(prev => ({ ...prev, image_url: res.data.image_url }));
      }
    } catch (error) {
      console.error('Image generation error:', error);
    } finally {
      setGeneratingImage(false);
    }
  };

  // Handle base64 image paste — convert to uploaded file
  const handleImageUrlChange = async (value) => {
    if (value.startsWith('data:image')) {
      try {
        const res = await apiClient.post(`${API}/api/blogs/upload-base64-image`, { base64_data: value }, getAuthHeaders());
        setBlogForm(prev => ({ ...prev, image_url: res.data.image_url }));
        alert('Image upload हो गई! ✅');
      } catch {
        setBlogForm(prev => ({ ...prev, image_url: value }));
      }
    } else {
      setBlogForm(prev => ({ ...prev, image_url: value }));
    }
  };

  const handleGenerateBlog = async () => {
    setGeneratingBlog(true);
    setShowAIPrompt(false);
    try {
      const body = {};
      if (aiTopic.trim()) body.topic = aiTopic.trim();
      if (aiLang) body.language = aiLang;
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/api/blogs/generate`, body, { 
        headers: { Authorization: `Bearer ${token}` },
        timeout: 180000
      });
      alert(`Blog draft created: "${res.data.title}"\nApprove it from the Blogs tab.`);
      setAITopic('');
      fetchBlogs();
    } catch (error) {
      alert('Blog generation failed. Please try again.');
      console.error('Error generating blog:', error);
    } finally {
      setGeneratingBlog(false);
    }
  };

  const handleApproveBlog = async (blogId) => {
    try {
      await apiClient.put(`${API}/api/blogs/${blogId}/approve`, {}, getAuthHeaders());
      fetchBlogs();
    } catch (error) { console.error('Error approving blog:', error); }
  };

  const handleRejectBlog = async (blogId) => {
    if (window.confirm('Reject and delete this blog draft?')) {
      try {
        await apiClient.put(`${API}/api/blogs/${blogId}/reject`, {}, getAuthHeaders());
        fetchBlogs();
      } catch (error) { console.error('Error rejecting blog:', error); }
    }
  };

  const handleMarkAsRead = async (messageId) => {
    try { await apiClient.put(`${API}/api/contact/${messageId}/read`, {}, getAuthHeaders()); fetchMessages(); }
    catch (error) { console.error('Error marking as read:', error); }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const sidebarItems = [
    { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { id: 'analytics', icon: BarChart3, label: 'Analytics' },
    { id: 'crm', icon: Phone, label: 'CRM / Leads' },
    { id: 'consultations', icon: CalendarCheck, label: 'Consultations' },
    { id: 'blogs', icon: FileText, label: 'Blogs' },
    { id: 'reviews', icon: Star, label: 'Reviews' },
    { id: 'messages', icon: MessageSquare, label: 'Messages' },
    { id: 'kundli', icon: Star, label: 'Kundli Requests' },
    { id: 'security', icon: Shield, label: 'Security' },
  ];

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-surface border-b border-primary/10 flex items-center justify-between px-4 z-50">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
            <Sun className="w-4 h-4 text-background" />
          </div>
          <span className="font-heading text-lg text-text-main">Admin</span>
        </Link>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-text-main">
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Sidebar
          🕉️ Feb 07, 2026 P0 layout refactor:
          - Whole aside is now `flex flex-col` with `justify-between` so the
            footer NEVER overlaps the nav even when items grow or viewport
            shrinks.
          - Nav area is `flex-1 overflow-y-auto` so items scroll cleanly
            without pushing the footer off-screen.
          - Footer stack (user info / Acharya / Password / Logout) is a
            simple flex-column with 12px gaps — no absolute positioning. */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-40
        w-64 bg-surface border-r border-primary/10
        transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0
        transition-transform duration-300 pt-16 lg:pt-0
        flex flex-col
      `}>
        <div className="hidden lg:flex items-center gap-3 p-6 border-b border-primary/10 shrink-0">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
            <Sun className="w-5 h-5 text-background" />
          </div>
          <div>
            <h1 className="font-heading text-lg text-text-main">ViratAstro</h1>
            <p className="text-xs text-primary font-medium">Admin Panel</p>
          </div>
        </div>

        <nav className="p-4 space-y-2 flex-1 overflow-y-auto">
          {sidebarItems.map((item) => (
            <button
              key={item.id}
              onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
              data-testid={`nav-${item.id}`}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                activeTab === item.id
                  ? 'bg-primary/15 text-primary'
                  : 'text-text-muted hover:bg-surface-alt hover:text-text-main'
              }`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </button>
          ))}
        </nav>

        <div className="shrink-0 flex flex-col gap-3 p-4 border-t border-primary/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 shrink-0 rounded-full bg-primary/20 flex items-center justify-center text-primary font-semibold">
              {user?.name?.charAt(0) || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-text-main truncate">{user?.name}</p>
              <p className="text-xs text-text-muted break-all leading-snug">{user?.email}</p>
            </div>
          </div>
          <a
            href="/acharya-dashboard"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="admin-open-acharya-dashboard"
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary/15 text-primary border border-primary/40 hover:bg-primary/25 transition-colors font-medium"
          >
            <Star className="w-5 h-5" />
            Acharya Dashboard
            <span className="text-xs opacity-70">↗</span>
          </a>
          <button
            onClick={() => navigate('/admin/change-password')}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gold/10 text-gold rounded-xl hover:bg-gold/20 transition-colors"
            data-testid="change-password-btn"
          >
            🔐 पासवर्ड बदलें
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-500/10 text-red-400 rounded-xl hover:bg-red-500/20 transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 pt-16 lg:pt-0 min-h-screen">
        <div className="p-6 lg:p-8">
          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && (
            <div>
              <h2 className="font-heading text-3xl text-text-main mb-8">Dashboard</h2>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-6">
                <div className="glass-card p-6" data-testid="stats-blogs">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-primary/15 flex items-center justify-center">
                      <FileText className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <p className="text-3xl font-heading font-semibold text-text-main">{blogs.length}</p>
                      <p className="text-text-muted">Total Blogs</p>
                    </div>
                  </div>
                </div>
                <div className="glass-card p-6" data-testid="stats-messages">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-green-500/15 flex items-center justify-center">
                      <MessageSquare className="w-7 h-7 text-green-400" />
                    </div>
                    <div>
                      <p className="text-3xl font-heading font-semibold text-text-main">{messages.length}</p>
                      <p className="text-text-muted">Messages</p>
                    </div>
                  </div>
                </div>
                <div className="glass-card p-6" data-testid="stats-unread">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-blue-500/15 flex items-center justify-center">
                      <Mail className="w-7 h-7 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-3xl font-heading font-semibold text-text-main">
                        {messages.filter(m => !m.read).length}
                      </p>
                      <p className="text-text-muted">Unread</p>
                    </div>
                  </div>
                </div>
                <div className="glass-card p-6" data-testid="stats-kundli">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-purple-500/15 flex items-center justify-center">
                      <Star className="w-7 h-7 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-3xl font-heading font-semibold text-text-main">{kundliRequests.length}</p>
                      <p className="text-text-muted">Kundli Requests</p>
                    </div>
                  </div>
                </div>
                <div className="glass-card p-6" data-testid="stats-consultations">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-orange-500/15 flex items-center justify-center">
                      <CalendarCheck className="w-7 h-7 text-orange-400" />
                    </div>
                    <div>
                      <p className="text-3xl font-heading font-semibold text-text-main">{consultations.length}</p>
                      <p className="text-text-muted">Consultations</p>
                    </div>
                  </div>
                </div>
              </div>
              {/* CRM Stats Row */}
              {crmStats && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
                  <div className="glass-card p-4 border-l-4 border-l-red-500">
                    <p className="text-2xl font-heading text-red-400">{crmStats.new}</p>
                    <p className="text-text-muted text-sm">New Leads</p>
                  </div>
                  <div className="glass-card p-4 border-l-4 border-l-yellow-500">
                    <p className="text-2xl font-heading text-yellow-400">{crmStats.contacted}</p>
                    <p className="text-text-muted text-sm">Contacted</p>
                  </div>
                  <div className="glass-card p-4 border-l-4 border-l-blue-500">
                    <p className="text-2xl font-heading text-blue-400">{crmStats.interested}</p>
                    <p className="text-text-muted text-sm">Interested</p>
                  </div>
                  <div className="glass-card p-4 border-l-4 border-l-green-500">
                    <p className="text-2xl font-heading text-green-400">{crmStats.converted}</p>
                    <p className="text-text-muted text-sm">Converted ({crmStats.conversion_rate}%)</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <div data-testid="analytics-tab">
              <div className="flex items-center justify-between mb-8">
                <h2 className="font-heading text-3xl text-text-main">Analytics Dashboard</h2>
                <button onClick={fetchAnalytics} className="btn-gold-outline text-sm px-4 py-2" data-testid="refresh-analytics">
                  Refresh
                </button>
              </div>

              {!analyticsData ? (
                <div className="text-center text-text-muted py-20">Loading analytics...</div>
              ) : (
                <>
                  {/* Top Stats Cards */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    <div className="glass-card p-5" data-testid="analytics-total-leads">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-yellow-500/15 flex items-center justify-center">
                          <Users className="w-6 h-6 text-yellow-400" />
                        </div>
                        <div>
                          <p className="text-2xl font-heading text-text-main">{analyticsData.leads.total}</p>
                          <p className="text-text-muted text-xs">Total Leads</p>
                        </div>
                      </div>
                    </div>
                    <div className="glass-card p-5" data-testid="analytics-chatbot-sessions">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-purple-500/15 flex items-center justify-center">
                          <MessageCircle className="w-6 h-6 text-purple-400" />
                        </div>
                        <div>
                          <p className="text-2xl font-heading text-text-main">{analyticsData.chatbot.total}</p>
                          <p className="text-text-muted text-xs">Chatbot Sessions</p>
                          <p className="text-green-400 text-xs">+{analyticsData.chatbot.today} today</p>
                        </div>
                      </div>
                    </div>
                    <div className="glass-card p-5" data-testid="analytics-blog-views">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-blue-500/15 flex items-center justify-center">
                          <Eye className="w-6 h-6 text-blue-400" />
                        </div>
                        <div>
                          <p className="text-2xl font-heading text-text-main">{analyticsData.blogs.total_views}</p>
                          <p className="text-text-muted text-xs">Blog Views</p>
                          <p className="text-green-400 text-xs">+{analyticsData.blogs.today_views} today</p>
                        </div>
                      </div>
                    </div>
                    <div className="glass-card p-5" data-testid="analytics-conversion">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-green-500/15 flex items-center justify-center">
                          <TrendingUp className="w-6 h-6 text-green-400" />
                        </div>
                        <div>
                          <p className="text-2xl font-heading text-text-main">{analyticsData.leads.conversion_rate}%</p>
                          <p className="text-text-muted text-xs">Conversion Rate</p>
                          <p className="text-yellow-400 text-xs">{analyticsData.leads.converted} converted</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Charts Row */}
                  <div className="grid lg:grid-cols-2 gap-6 mb-8">
                    {/* Daily Leads Chart */}
                    <div className="glass-card p-6" data-testid="analytics-daily-chart">
                      <h3 className="text-lg font-heading text-text-main mb-4">Daily Leads (Last 7 Days)</h3>
                      <div className="flex items-end gap-2 h-40">
                        {analyticsData.daily_leads.map((d, i) => {
                          const maxCount = Math.max(...analyticsData.daily_leads.map(x => x.count), 1);
                          const height = Math.max((d.count / maxCount) * 100, 8);
                          return (
                            <div key={i} className="flex-1 flex flex-col items-center gap-1">
                              <span className="text-xs text-yellow-400 font-semibold">{d.count}</span>
                              <div
                                className="w-full rounded-t-lg bg-gradient-to-t from-yellow-700 to-yellow-500 transition-all"
                                style={{ height: `${height}%` }}
                              />
                              <span className="text-xs text-text-muted">{d.day}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Leads by Service */}
                    <div className="glass-card p-6" data-testid="analytics-service-chart">
                      <h3 className="text-lg font-heading text-text-main mb-4">Leads by Service</h3>
                      <div className="space-y-3">
                        {analyticsData.leads_by_service.map((s, i) => {
                          const maxS = Math.max(...analyticsData.leads_by_service.map(x => x.count), 1);
                          const width = (s.count / maxS) * 100;
                          const colors = ['bg-yellow-500', 'bg-purple-500', 'bg-blue-500', 'bg-green-500', 'bg-pink-500', 'bg-orange-500', 'bg-cyan-500', 'bg-red-500'];
                          return (
                            <div key={i}>
                              <div className="flex justify-between text-sm mb-1">
                                <span className="text-text-muted truncate max-w-[200px]">{s.service}</span>
                                <span className="text-text-main font-semibold">{s.count}</span>
                              </div>
                              <div className="w-full h-2 bg-surface-alt rounded-full overflow-hidden">
                                <div className={`h-full rounded-full ${colors[i % colors.length]}`} style={{ width: `${width}%` }} />
                              </div>
                            </div>
                          );
                        })}
                        {analyticsData.leads_by_service.length === 0 && (
                          <p className="text-text-muted text-sm text-center py-4">No data yet</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Bottom Row */}
                  <div className="grid lg:grid-cols-2 gap-6">
                    {/* Top Blogs */}
                    <div className="glass-card p-6" data-testid="analytics-top-blogs">
                      <h3 className="text-lg font-heading text-text-main mb-4">Top Blogs (by Views)</h3>
                      {analyticsData.blogs.top_blogs.length > 0 ? (
                        <div className="space-y-3">
                          {analyticsData.blogs.top_blogs.map((b, i) => (
                            <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-surface-alt/50">
                              <span className="text-yellow-400 font-heading text-lg w-6">#{i + 1}</span>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-text-main truncate">{b.title}</p>
                              </div>
                              <div className="flex items-center gap-1 text-blue-400">
                                <Eye className="w-4 h-4" />
                                <span className="text-sm font-semibold">{b.views}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-text-muted text-sm text-center py-4">No blog views tracked yet</p>
                      )}
                    </div>

                    {/* Recent Leads */}
                    <div className="glass-card p-6" data-testid="analytics-recent-leads">
                      <h3 className="text-lg font-heading text-text-main mb-4">Recent Inquiries</h3>
                      {analyticsData.recent_leads.length > 0 ? (
                        <div className="space-y-3">
                          {analyticsData.recent_leads.map((l, i) => (
                            <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-surface-alt/50">
                              <div className="w-9 h-9 rounded-full bg-yellow-500/20 flex items-center justify-center text-yellow-400 font-semibold text-sm">
                                {l.name?.charAt(0) || '?'}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-text-main truncate">{l.name}</p>
                                <p className="text-xs text-text-muted truncate">{l.service || 'General'}</p>
                              </div>
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                l.status === 'new' ? 'bg-red-500/20 text-red-400' :
                                l.status === 'contacted' ? 'bg-yellow-500/20 text-yellow-400' :
                                l.status === 'interested' ? 'bg-blue-500/20 text-blue-400' :
                                'bg-green-500/20 text-green-400'
                              }`}>{l.status}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-text-muted text-sm text-center py-4">No leads yet</p>
                      )}
                    </div>
                  </div>

                  {/* Summary Footer */}
                  <div className="grid grid-cols-3 gap-4 mt-6">
                    <div className="glass-card p-4 text-center border-t-2 border-t-yellow-500">
                      <p className="text-xl font-heading text-yellow-400">{analyticsData.blogs.total}</p>
                      <p className="text-text-muted text-xs">Published Blogs</p>
                    </div>
                    <div className="glass-card p-4 text-center border-t-2 border-t-purple-500">
                      <p className="text-xl font-heading text-purple-400">{analyticsData.messages.unread}</p>
                      <p className="text-text-muted text-xs">Unread Messages</p>
                    </div>
                    <div className="glass-card p-4 text-center border-t-2 border-t-blue-500">
                      <p className="text-xl font-heading text-blue-400">{analyticsData.kundli.total}</p>
                      <p className="text-text-muted text-xs">Kundli Requests</p>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* CRM / Leads Tab */}
          {activeTab === 'crm' && (
            <div>
              <h2 className="font-heading text-3xl text-text-main mb-6">CRM / Lead Management</h2>

              {/* CRM Stats */}
              {crmStats && (
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                  <div className="glass-card p-4 text-center" data-testid="crm-total">
                    <p className="text-3xl font-heading text-text-main">{crmStats.total_leads}</p>
                    <p className="text-text-muted text-xs">Total Leads</p>
                  </div>
                  <div className="glass-card p-4 text-center border-t-2 border-t-red-500" data-testid="crm-new">
                    <p className="text-3xl font-heading text-red-400">{crmStats.new}</p>
                    <p className="text-text-muted text-xs">New</p>
                  </div>
                  <div className="glass-card p-4 text-center border-t-2 border-t-yellow-500">
                    <p className="text-3xl font-heading text-yellow-400">{crmStats.contacted}</p>
                    <p className="text-text-muted text-xs">Contacted</p>
                  </div>
                  <div className="glass-card p-4 text-center border-t-2 border-t-blue-500">
                    <p className="text-3xl font-heading text-blue-400">{crmStats.interested}</p>
                    <p className="text-text-muted text-xs">Interested</p>
                  </div>
                  <div className="glass-card p-4 text-center border-t-2 border-t-green-500">
                    <p className="text-3xl font-heading text-green-400">{crmStats.converted} ({crmStats.conversion_rate}%)</p>
                    <p className="text-text-muted text-xs">Converted</p>
                  </div>
                </div>
              )}

              {/* Manual WhatsApp Send */}
              <div className="glass-card p-5 mb-6" data-testid="manual-whatsapp">
                <h3 className="text-text-main font-heading text-lg mb-4">Send WhatsApp Message</h3>
                <div className="flex flex-col md:flex-row gap-3">
                  <input type="text" placeholder="Phone number (+91...)" value={sendWAPhone}
                    onChange={e => setSendWAPhone(e.target.value)} className="md:w-48" data-testid="wa-phone-input" />
                  <input type="text" placeholder="Type your message..." value={sendWAMessage}
                    onChange={e => setSendWAMessage(e.target.value)} className="flex-1" data-testid="wa-message-input" />
                  <button onClick={sendManualWA} disabled={sendingWA || !sendWAPhone || !sendWAMessage}
                    className="btn-gold whitespace-nowrap disabled:opacity-50" data-testid="wa-send-btn">
                    {sendingWA ? 'Sending...' : 'Send WhatsApp'}
                  </button>
                </div>
              </div>

              {/* Follow-up Queue */}
              <div className="glass-card p-5 mb-6" data-testid="follow-up-queue">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-text-main font-heading text-lg">Follow-up Queue ({followUps.filter(f => !f.sent).length} pending)</h3>
                  <button onClick={processFollowUps} className="text-sm bg-blue-600/20 text-blue-400 px-4 py-2 rounded-lg hover:bg-blue-600/30 transition-colors" data-testid="process-followups-btn">
                    Process Now
                  </button>
                </div>
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {followUps.slice(0, 30).map((fu, i) => {
                    const dayLabels = {
                      1: 'Check-in',
                      2: 'Case Study',
                      3: 'Urgency',
                      4: 'Scarcity',
                      5: 'Last Reminder',
                      6: 'Farewell'
                    };
                    return (
                    <div key={i} className={`flex items-center justify-between py-2 px-3 rounded-lg text-sm ${fu.sent ? 'bg-green-500/5 text-text-muted' : 'bg-yellow-500/5'}`}>
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className="text-text-main truncate">{fu.name}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-purple/20 text-purple-light whitespace-nowrap">Day {fu.day} - {dayLabels[fu.day] || `Day ${fu.day}`}</span>
                      </div>
                      <div className="flex items-center gap-2 ml-2 shrink-0">
                        <span className="text-text-muted text-xs">{new Date(fu.send_at).toLocaleString('en-IN')}</span>
                        {fu.sent ? <span className="text-green-400 text-xs font-medium">Sent</span> : <span className="text-yellow-400 text-xs font-medium">Pending</span>}
                      </div>
                    </div>
                    );
                  })}
                  {followUps.length === 0 && <p className="text-text-muted text-center py-4">No follow-ups scheduled</p>}
                </div>
              </div>

              {/* Lead List */}
              <div className="glass-card p-5">
                <h3 className="text-text-main font-heading text-lg mb-4">All Leads</h3>
                <div className="space-y-3">
                  {consultations.map(lead => (
                    <div key={lead.id} className={`p-4 rounded-xl border transition-all ${
                      lead.status === 'new' ? 'bg-red-500/5 border-red-500/20' :
                      lead.status === 'contacted' ? 'bg-yellow-500/5 border-yellow-500/20' :
                      lead.status === 'interested' ? 'bg-blue-500/5 border-blue-500/20' :
                      lead.status === 'converted' ? 'bg-green-500/5 border-green-500/20' :
                      'bg-white/3 border-white/5'
                    }`} data-testid={`lead-${lead.id}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="text-text-main font-medium">{lead.name}</h4>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              lead.status === 'new' ? 'bg-red-500/20 text-red-400' :
                              lead.status === 'contacted' ? 'bg-yellow-500/20 text-yellow-400' :
                              lead.status === 'interested' ? 'bg-blue-500/20 text-blue-400' :
                              'bg-green-500/20 text-green-400'
                            }`}>{lead.status?.toUpperCase()}</span>
                          </div>
                          <p className="text-text-muted text-sm">{lead.phone} | {lead.email}</p>
                          <p className="text-text-muted text-xs mt-1">Service: {lead.service} | {formatDate(lead.created_at)}</p>
                          {lead.message && <p className="text-text-muted text-xs mt-1 italic">"{lead.message}"</p>}
                          {lead.notes && <p className="text-blue-300 text-xs mt-1">Notes: {lead.notes}</p>}
                          {lead.tags && lead.tags.length > 0 && (
                            <div className="flex gap-1 mt-1">{lead.tags.map((t, i) => (
                              <span key={i} className="text-xs bg-purple-500/10 text-purple-300 px-2 py-0.5 rounded">{t}</span>
                            ))}</div>
                          )}
                        </div>
                        <div className="flex flex-col gap-1">
                          <select
                            value={lead.status || 'new'}
                            onChange={e => updateConsultationStatus(lead.id, e.target.value)}
                            className="text-xs bg-surface border border-white/10 rounded px-2 py-1 text-text-main"
                            data-testid={`lead-status-${lead.id}`}>
                            <option value="new">New</option>
                            <option value="contacted">Contacted</option>
                            <option value="interested">Interested</option>
                            <option value="converted">Converted</option>
                          </select>
                          <button onClick={() => {
                            const note = prompt('Add note for this lead:', lead.notes || '');
                            if (note !== null) updateLeadDetails(lead.id, { notes: note });
                          }} className="text-xs text-blue-400 hover:underline" data-testid={`lead-note-${lead.id}`}>
                            Add Note
                          </button>
                          <button onClick={() => {
                            setSendWAPhone(lead.phone);
                            setSendWAMessage('');
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }} className="text-xs text-green-400 hover:underline">
                            WhatsApp
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          {activeTab === 'blogs' && (
            <div>
              <SeoBackfillBanner />
              <div className="flex items-center justify-between mb-8">
                <h2 className="font-heading text-3xl text-text-main">Blogs</h2>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => navigate('/admin/roadmap')}
                    className="border border-gold/40 text-gold px-4 py-2 rounded-xl flex items-center gap-2 hover:bg-gold/10 transition"
                    data-testid="open-roadmap-btn"
                  >
                    🗺️ Roadmap
                  </button>
                  <button
                    onClick={() => navigate('/admin/eeat-audit')}
                    className="border border-emerald-400/40 text-emerald-300 px-4 py-2 rounded-xl flex items-center gap-2 hover:bg-emerald-500/10 transition"
                    data-testid="open-eeat-audit-btn"
                  >
                    🎯 EEAT Audit
                  </button>
                  <button
                    onClick={() => navigate('/admin/conversion-audit')}
                    className="border border-purple-400/40 text-purple-300 px-4 py-2 rounded-xl flex items-center gap-2 hover:bg-purple-500/10 transition"
                    data-testid="open-conversion-audit-btn"
                  >
                    💰 Conversion Audit
                  </button>
                  <button
                    onClick={() => navigate('/admin/integrations')}
                    className="border border-gold/40 text-gold px-4 py-2 rounded-xl flex items-center gap-2 hover:bg-gold/10 transition"
                    data-testid="open-integrations-btn"
                  >
                    🔌 Integrations
                  </button>
                  <button
                    onClick={() => navigate('/admin/auto-blog-engine')}
                    className="bg-gradient-to-r from-gold to-amber-500 text-[#0F172A] font-semibold px-4 py-2 rounded-xl flex items-center gap-2 hover:brightness-110 transition"
                    data-testid="open-auto-blog-engine-btn"
                  >
                    <Star className="w-4 h-4" /> Auto Blog Engine v3
                  </button>
                  <button
                    onClick={() => setShowAIPrompt(true)}
                    disabled={generatingBlog}
                    className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-2 rounded-xl flex items-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50"
                    data-testid="ai-generate-blog-btn"
                  >
                    {generatingBlog ? (
                      <><span className="animate-spin">⟳</span> AI Generating...</>
                    ) : (
                      <><Star className="w-4 h-4" /> AI Generate (Legacy)</>
                    )}
                  </button>
                  <button
                    onClick={() => { setShowBlogForm(true); setEditingBlog(null); setBlogForm({ title: '', excerpt: '', content: '', image_url: '', category: 'General', published: false, scheduled_at: '' }); }}
                    className="btn-gold flex items-center gap-2"
                    data-testid="add-blog-btn"
                  >
                    <Plus className="w-4 h-4" /> New Blog
                  </button>
                </div>
              </div>

              {/* AI Topic Prompt Popup */}
              {showAIPrompt && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[10000] p-4">
                  <div className="bg-surface rounded-2xl p-6 w-full max-w-md border border-primary/10" data-testid="ai-topic-popup">
                    <h3 className="font-heading text-xl text-text-main mb-4">AI Blog Generate</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm text-text-muted mb-2">Topic (Optional)</label>
                        <input type="text" value={aiTopic} onChange={(e) => setAITopic(e.target.value)}
                          placeholder="e.g., शनि साढ़ेसाती के उपाय / Career astrology tips"
                          className="w-full" data-testid="ai-topic-input" />
                        <p className="text-xs text-text-muted mt-1">Blank chhod dein toh AI khud topic choose karega</p>
                      </div>
                      <div>
                        <label className="block text-sm text-text-muted mb-2">Language</label>
                        <div className="flex gap-2">
                          <button type="button" onClick={() => setAILang('hi')}
                            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${aiLang === 'hi' ? 'bg-gold/20 text-gold border border-gold/30' : 'bg-white/5 text-text-muted border border-white/10'}`}
                            data-testid="ai-lang-hi">Hindi</button>
                          <button type="button" onClick={() => setAILang('en')}
                            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${aiLang === 'en' ? 'bg-gold/20 text-gold border border-gold/30' : 'bg-white/5 text-text-muted border border-white/10'}`}
                            data-testid="ai-lang-en">English</button>
                        </div>
                      </div>
                      <div className="flex gap-3 pt-2">
                        <button onClick={handleGenerateBlog} className="btn-gold flex-1 flex items-center justify-center gap-2" data-testid="ai-generate-confirm-btn">
                          <Star className="w-4 h-4" /> Generate
                        </button>
                        <button onClick={() => setShowAIPrompt(false)} className="btn-gold-outline flex-1" data-testid="ai-cancel-btn">Cancel</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {showBlogForm && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[10000] p-4">
                  <div className="bg-surface rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-primary/10">
                    <h3 className="font-heading text-2xl text-text-main mb-6">
                      {editingBlog ? 'Edit Blog' : 'New Blog'}
                    </h3>
                    <form onSubmit={handleBlogSubmit} className="space-y-4">
                      <div>
                        <label className="block text-sm text-text-muted mb-2 font-medium">Title</label>
                        <input type="text" value={blogForm.title} onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value })} required data-testid="blog-title-input" />
                      </div>
                      <div>
                        <label className="block text-sm text-text-muted mb-2 font-medium">Category</label>
                        <select value={blogForm.category} onChange={(e) => setBlogForm({ ...blogForm, category: e.target.value })} data-testid="blog-category-select">
                          <option value="Astrology">Astrology</option>
                          <option value="Horoscope">Horoscope</option>
                          <option value="Kundli">Kundli</option>
                          <option value="Remedies">Remedies</option>
                          <option value="Vastu">Vastu</option>
                          <option value="Numerology">Numerology</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-text-muted mb-2 font-medium">Image URL</label>
                        <div className="flex gap-2">
                          <input type="text" value={blogForm.image_url} onChange={(e) => handleImageUrlChange(e.target.value)} placeholder="https://... या AI से बनाएं →" className="flex-1" data-testid="blog-image-input" />
                          <button 
                            type="button" 
                            onClick={handleGenerateImage} 
                            disabled={generatingImage}
                            className={`px-4 py-2 rounded-xl font-medium text-sm whitespace-nowrap transition-all ${generatingImage ? 'bg-gold/30 text-gold/60 cursor-wait' : 'bg-gold text-background hover:bg-gold/90'}`}
                            data-testid="ai-image-generate-btn"
                          >
                            {generatingImage ? '⏳ बन रही है...' : '🎨 AI Image बनाओ'}
                          </button>
                        </div>
                        {blogForm.image_url && (
                          <div className="mt-2 rounded-lg overflow-hidden border border-gold/20">
                            <img src={blogForm.image_url.startsWith('/') ? `${API}${blogForm.image_url}` : blogForm.image_url} alt="Preview" className="w-full h-32 object-cover" />
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm text-text-muted mb-2 font-medium">Excerpt</label>
                        <textarea value={blogForm.excerpt} onChange={(e) => setBlogForm({ ...blogForm, excerpt: e.target.value })} rows={2} required data-testid="blog-excerpt-input" />
                      </div>
                      <div>
                        <label className="block text-sm text-text-muted mb-2 font-medium">Content (HTML)</label>
                        <textarea value={blogForm.content} onChange={(e) => setBlogForm({ ...blogForm, content: e.target.value })} rows={8} required data-testid="blog-content-input" />
                      </div>
                      {/* Publish / Schedule Options */}
                      <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                        <label className="block text-sm text-text-muted mb-3 font-medium">Publish Options</label>
                        <div className="flex flex-wrap gap-3">
                          <button type="button" onClick={() => setBlogForm(prev => ({ ...prev, published: false, scheduled_at: '' }))}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${!blogForm.published && !blogForm.scheduled_at ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' : 'bg-white/5 text-text-muted border border-white/10 hover:border-yellow-500/20'}`}
                            data-testid="blog-draft-btn">
                            Draft (Save Only)
                          </button>
                          <button type="button" onClick={() => setBlogForm(prev => ({ ...prev, published: true, scheduled_at: '' }))}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${blogForm.published && !blogForm.scheduled_at ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-white/5 text-text-muted border border-white/10 hover:border-green-500/20'}`}
                            data-testid="blog-publish-now-btn">
                            Publish Now
                          </button>
                          <button type="button" onClick={() => setBlogForm(prev => ({ ...prev, published: false, scheduled_at: prev.scheduled_at || new Date(Date.now() + 86400000).toISOString().slice(0,16) }))}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${blogForm.scheduled_at ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'bg-white/5 text-text-muted border border-white/10 hover:border-blue-500/20'}`}
                            data-testid="blog-schedule-btn">
                            Schedule
                          </button>
                        </div>
                        {blogForm.scheduled_at && (
                          <div className="mt-3">
                            <label className="block text-xs text-text-muted mb-1">Schedule Date & Time</label>
                            <input type="datetime-local" value={blogForm.scheduled_at} 
                              onChange={(e) => setBlogForm(prev => ({ ...prev, scheduled_at: e.target.value, published: false }))} 
                              className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-sm text-text-main w-full"
                              data-testid="blog-schedule-datetime" />
                          </div>
                        )}
                      </div>
                      <div className="flex gap-3 pt-4">
                        <button type="submit" className="btn-gold flex-1" data-testid="blog-submit-btn">
                          {editingBlog ? 'Update' : 'Create'}
                        </button>
                        <button type="button" onClick={() => setShowPreview(true)} className="px-4 py-2 rounded-xl bg-blue-500/20 text-blue-400 border border-blue-500/30 hover:bg-blue-500/30 transition-all text-sm font-medium" data-testid="blog-preview-btn">
                          Preview
                        </button>
                        <button type="button" onClick={() => setShowBlogForm(false)} className="btn-gold-outline flex-1" data-testid="blog-cancel-btn">
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* Blog Preview Modal */}
              {showPreview && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[10001] p-4">
                  <div className="bg-background rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto border border-primary/20">
                    <div className="sticky top-0 bg-background/95 backdrop-blur-sm border-b border-primary/10 p-4 flex items-center justify-between z-10">
                      <h3 className="font-heading text-xl text-text-main">Blog Preview</h3>
                      <button onClick={() => setShowPreview(false)} className="text-text-muted hover:text-white px-3 py-1 rounded-lg hover:bg-white/10" data-testid="close-preview-btn">Close</button>
                    </div>
                    <div className="p-6">
                      {blogForm.image_url && (
                        <div className="rounded-2xl overflow-hidden mb-6">
                          <img src={blogForm.image_url.startsWith('/') ? `${API}${blogForm.image_url}` : blogForm.image_url} alt="" className="w-full h-64 object-cover" />
                        </div>
                      )}
                      <span className="text-xs bg-gold/20 text-gold px-3 py-1 rounded-full">{blogForm.category}</span>
                      <h1 className="font-heading text-3xl text-text-main mt-4 mb-4">{blogForm.title || 'Untitled'}</h1>
                      <p className="text-text-muted text-lg mb-6">{blogForm.excerpt}</p>
                      <div className="prose prose-lg prose-invert max-w-none text-text-muted blog-content" dangerouslySetInnerHTML={safeHtml(renderBlogContent(blogForm.content))} />
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                {blogs.map((blog) => (
                  <div key={blog.id} className={`glass-card p-4 ${!blog.published ? 'border-l-4 border-l-yellow-500' : ''}`} data-testid={`blog-item-${blog.id}`}>
                    <div className="flex items-start gap-4">
                      {blog.image_url && (
                        <img src={blog.image_url.startsWith('/') ? `${API}${blog.image_url}` : blog.image_url} alt="" className="w-16 h-16 rounded-xl object-cover shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="text-text-main font-medium">{blog.title}</h4>
                          {!blog.published && !blog.scheduled_at && <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded-full whitespace-nowrap">Draft</span>}
                          {!blog.published && blog.scheduled_at && <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full whitespace-nowrap">Scheduled: {new Date(blog.scheduled_at).toLocaleString('hi-IN', {day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'})}</span>}
                          {blog.published && <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full whitespace-nowrap">Published</span>}
                          {blog.auto_generated && <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full whitespace-nowrap">AI Generated</span>}
                        </div>
                        <p className="text-sm text-text-muted mt-1 line-clamp-1">{blog.excerpt}</p>
                        <p className="text-xs text-primary mt-1">{blog.category} - {formatDate(blog.created_at)}</p>
                      </div>
                    </div>
                    {/* Action Buttons - clearly visible below content */}
                    <div className="flex gap-2 mt-3 pt-3 border-t border-white/5">
                      {!blog.published && (
                        <>
                          <button onClick={() => handleApproveBlog(blog.id)} 
                            className="flex items-center gap-1.5 px-4 py-2 bg-green-500/15 text-green-400 rounded-lg hover:bg-green-500/25 transition-colors text-sm font-medium" 
                            title="Approve & Publish" data-testid={`approve-blog-${blog.id}`}>
                            <CalendarCheck className="w-4 h-4" /> Approve
                          </button>
                          <button onClick={() => handleRejectBlog(blog.id)} 
                            className="flex items-center gap-1.5 px-4 py-2 bg-red-500/15 text-red-400 rounded-lg hover:bg-red-500/25 transition-colors text-sm font-medium" 
                            title="Reject & Delete" data-testid={`reject-blog-${blog.id}`}>
                            <X className="w-4 h-4" /> Reject
                          </button>
                        </>
                      )}
                      <button onClick={() => handleEditBlog(blog)} className="flex items-center gap-1.5 px-3 py-2 text-text-muted hover:text-primary hover:bg-white/5 rounded-lg transition-colors text-sm" data-testid={`edit-blog-${blog.id}`}>
                        <Edit className="w-4 h-4" /> Edit
                      </button>
                      <button onClick={() => handleDeleteBlog(blog.id)} className="flex items-center gap-1.5 px-3 py-2 text-text-muted hover:text-red-400 hover:bg-red-500/5 rounded-lg transition-colors text-sm" data-testid={`delete-blog-${blog.id}`}>
                        <Trash2 className="w-4 h-4" /> Delete
                      </button>
                    </div>
                  </div>
                ))}
                {blogs.length === 0 && (
                  <p className="text-text-muted text-center py-8">No blogs yet. Create your first blog!</p>
                )}
              </div>
            </div>
          )}

          {/* Reviews Tab */}
          {activeTab === 'reviews' && (
            <div data-testid="reviews-tab">
              <h2 className="font-heading text-3xl text-text-main mb-6">Manage Google Reviews</h2>

              {/* Add Review Form */}
              <div className="glass-card p-6 mb-8" data-testid="add-review-form">
                <h3 className="text-lg text-text-main font-semibold mb-4">Add New Review</h3>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <input type="text" placeholder="Reviewer Name" value={newReview.name}
                    onChange={e => setNewReview({...newReview, name: e.target.value})}
                    className="px-4 py-2.5 rounded-lg bg-surface-alt border border-surface-alt text-text-main placeholder-text-muted/50 focus:border-gold outline-none"
                    data-testid="review-name-input" />
                  <input type="text" placeholder="Location (e.g. Delhi)" value={newReview.location}
                    onChange={e => setNewReview({...newReview, location: e.target.value})}
                    className="px-4 py-2.5 rounded-lg bg-surface-alt border border-surface-alt text-text-main placeholder-text-muted/50 focus:border-gold outline-none"
                    data-testid="review-location-input" />
                </div>
                <textarea placeholder="Review message..." rows={3} value={newReview.message}
                  onChange={e => setNewReview({...newReview, message: e.target.value})}
                  className="w-full px-4 py-2.5 rounded-lg bg-surface-alt border border-surface-alt text-text-main placeholder-text-muted/50 focus:border-gold outline-none mb-4"
                  data-testid="review-message-input" />
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex items-center gap-2">
                    <label className="text-text-muted text-sm">Rating:</label>
                    <select value={newReview.rating} onChange={e => setNewReview({...newReview, rating: Number(e.target.value)})}
                      className="px-3 py-1.5 rounded-lg bg-surface-alt border border-surface-alt text-text-main outline-none">
                      <option value={5}>5 Stars</option>
                      <option value={4}>4 Stars</option>
                      <option value={3}>3 Stars</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-text-muted text-sm">Source:</label>
                    <select value={newReview.source} onChange={e => setNewReview({...newReview, source: e.target.value})}
                      className="px-3 py-1.5 rounded-lg bg-surface-alt border border-surface-alt text-text-main outline-none">
                      <option value="google">Google</option>
                      <option value="justdial">Justdial</option>
                      <option value="website">Website</option>
                    </select>
                  </div>
                </div>
                <button onClick={addReview} className="btn-gold px-6 py-2.5" data-testid="add-review-btn">
                  Add Review
                </button>
              </div>

              {/* Reviews List */}
              <div className="grid md:grid-cols-2 gap-4">
                {reviewsList.map((r, i) => (
                  <div key={r.id || i} className="glass-card p-5" data-testid={`admin-review-${i}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-text-main font-medium">{r.name}</p>
                        <p className="text-xs text-text-muted">{r.location} | {r.source}</p>
                      </div>
                      <button onClick={() => deleteReview(r.id)} className="text-red-400 hover:text-red-300 p-1" data-testid={`delete-review-${i}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex gap-0.5 mb-2">
                      {[...Array(r.rating || 5)].map((_, j) => <Star key={j} className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />)}
                    </div>
                    <p className="text-text-muted text-sm italic">"{r.message}"</p>
                  </div>
                ))}
                {reviewsList.length === 0 && (
                  <p className="text-text-muted text-center col-span-2 py-10">No reviews added yet. Add reviews from Google/Justdial above.</p>
                )}
              </div>
            </div>
          )}

          {/* Messages Tab */}
          {activeTab === 'messages' && (
            <div>
              <h2 className="font-heading text-3xl text-text-main mb-8">Messages</h2>
              <div className="grid lg:grid-cols-2 gap-6">
                <div className="space-y-3">
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      onClick={() => { setSelectedMessage(msg); handleMarkAsRead(msg.id); }}
                      className={`glass-card p-4 cursor-pointer transition-all hover:border-primary/30 ${
                        selectedMessage?.id === msg.id ? 'border-primary/50 ring-2 ring-primary/20' : ''
                      } ${!msg.read ? 'border-l-4 border-l-primary' : ''}`}
                      data-testid={`message-item-${msg.id}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="text-text-main font-medium">{msg.name}</h4>
                        {!msg.read && <span className="w-2 h-2 rounded-full bg-primary"></span>}
                      </div>
                      <p className="text-sm text-text-muted truncate">{msg.subject}</p>
                      <p className="text-xs text-primary mt-2">{formatDate(msg.created_at)}</p>
                    </div>
                  ))}
                  {messages.length === 0 && (
                    <p className="text-text-muted text-center py-8">No messages yet</p>
                  )}
                </div>

                {selectedMessage && (
                  <div className="glass-card p-6" data-testid="message-detail">
                    <div className="flex items-start justify-between mb-6">
                      <div>
                        <h3 className="text-xl text-text-main font-medium">{selectedMessage.name}</h3>
                        <div className="flex items-center gap-4 mt-2 text-sm text-text-muted">
                          <span className="flex items-center gap-1"><Mail className="w-4 h-4" /> {selectedMessage.email}</span>
                          {selectedMessage.phone && (
                            <span className="flex items-center gap-1"><Phone className="w-4 h-4" /> {selectedMessage.phone}</span>
                          )}
                        </div>
                      </div>
                      <button onClick={() => handleDeleteMessage(selectedMessage.id)} className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors" data-testid="delete-message-btn">
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="mb-4">
                      <p className="text-xs text-text-muted mb-1">Service</p>
                      <p className="text-text-main font-medium">{selectedMessage.subject}</p>
                    </div>
                    <div className="mb-4">
                      <p className="text-xs text-text-muted mb-1">Message</p>
                      <p className="text-text-main whitespace-pre-wrap">{selectedMessage.message}</p>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-text-muted pt-4 border-t border-primary/10">
                      <Calendar className="w-4 h-4" /> {formatDate(selectedMessage.created_at)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Kundli Requests Tab */}
          {activeTab === 'kundli' && (
            <div>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <h2 className="font-heading text-3xl text-text-main">Kundli Requests ({kundliRequests.length})</h2>
                <a
                  href="/acharya-dashboard"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="admin-open-acharya-dashboard-cta"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-primary text-background font-medium hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20"
                >
                  <Star className="w-5 h-5" />
                  Open Acharya Dashboard
                  <span className="opacity-70">↗</span>
                </a>
              </div>
              <div className="space-y-3">
                {kundliRequests.map((req, idx) => (
                  <div key={req.id || idx} className="glass-card p-5" data-testid={`kundli-req-${idx}`}>
                    <div className="flex flex-col md:flex-row md:items-center gap-4">
                      <div className="flex-1">
                        <h4 className="text-text-main font-medium text-lg">{req.name}</h4>
                        <div className="flex flex-wrap gap-4 mt-2 text-sm text-text-muted">
                          <span className="flex items-center gap-1"><Calendar className="w-4 h-4 text-primary" /> DOB: {req.birth_date}</span>
                          <span>Time: {req.birth_time}</span>
                          <span>Place: {req.birth_place}</span>
                        </div>
                      </div>
                      <div className="flex gap-3 text-sm">
                        <span className="px-3 py-1 bg-primary/10 text-primary rounded-full">Sun: {req.sun_sign}</span>
                        <span className="px-3 py-1 bg-blue-500/10 text-blue-400 rounded-full">Moon: {req.moon_sign}</span>
                        <span className="px-3 py-1 bg-purple-500/10 text-purple-400 rounded-full">Asc: {req.ascendant}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-text-muted mt-3 pt-3 border-t border-primary/10">
                      <Calendar className="w-3 h-3" /> Calculated: {formatDate(req.created_at)}
                      {req.nakshatra && <span className="ml-2">| Nakshatra: {req.nakshatra}</span>}
                    </div>
                  </div>
                ))}
                {kundliRequests.length === 0 && (
                  <p className="text-text-muted text-center py-8">No kundli requests yet</p>
                )}
              </div>
            </div>
          )}

          {/* Consultations Tab */}
          {activeTab === 'consultations' && (
            <div>
              <h2 className="font-heading text-3xl text-text-main mb-8">Consultation Bookings ({consultations.length})</h2>
              <div className="space-y-4">
                {consultations.map((c, idx) => (
                  <div key={c.id || idx} className="glass-card p-5" data-testid={`consultation-${idx}`}>
                    <div className="flex flex-col md:flex-row md:items-start gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="text-text-main font-medium text-lg">{c.name}</h4>
                          <span className={`px-3 py-0.5 rounded-full text-xs font-medium ${
                            c.status === 'new' ? 'bg-green-500/15 text-green-400' :
                            c.status === 'contacted' ? 'bg-blue-500/15 text-blue-400' :
                            c.status === 'completed' ? 'bg-purple-500/15 text-purple-400' :
                            'bg-yellow-500/15 text-yellow-400'
                          }`}>
                            {c.status === 'new' ? 'NEW' : c.status?.toUpperCase()}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm text-text-muted">
                          <span className="flex items-center gap-1"><Phone className="w-4 h-4 text-primary" /> {c.phone}</span>
                          {c.email && <span className="flex items-center gap-1"><Mail className="w-4 h-4 text-primary" /> {c.email}</span>}
                        </div>
                        <div className="mt-2">
                          <span className="text-sm text-primary font-medium">{c.service}</span>
                        </div>
                        {c.message && (
                          <p className="text-sm text-text-muted mt-2 bg-surface-alt/50 rounded-lg p-3">{c.message}</p>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <a
                          href={`https://wa.me/${c.phone.replace(/[^0-9]/g, '')}?text=Namaste ${c.name}! Aapne ViratAstro par consultation book kiya tha.`}
                          target="_blank" rel="noopener noreferrer"
                          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg text-center transition-colors flex items-center gap-2"
                        >
                          <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                          Reply on WhatsApp
                        </a>
                        {c.status === 'new' && (
                          <button
                            onClick={() => updateConsultationStatus(c.id, 'contacted')}
                            className="px-4 py-2 bg-blue-500/15 text-blue-400 text-sm rounded-lg hover:bg-blue-500/25 transition-colors"
                            data-testid={`mark-contacted-${idx}`}
                          >
                            Mark Contacted
                          </button>
                        )}
                        {c.status === 'contacted' && (
                          <button
                            onClick={() => updateConsultationStatus(c.id, 'completed')}
                            className="px-4 py-2 bg-purple-500/15 text-purple-400 text-sm rounded-lg hover:bg-purple-500/25 transition-colors"
                          >
                            Mark Completed
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-text-muted mt-3 pt-3 border-t border-primary/10">
                      <Calendar className="w-3 h-3" /> Booked: {formatDate(c.created_at)}
                    </div>
                  </div>
                ))}
                {consultations.length === 0 && (
                  <p className="text-text-muted text-center py-8">No consultation bookings yet</p>
                )}
              </div>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <AdminSecurityTab />
          )}
        </div>
      </main>
    </div>
  );
}
